import { useState } from "react";
import { Mail, MessageCircle, MessageSquare, Monitor } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Prose } from "@/components/Prose";
import { cn } from "@/lib/cn";
import { blockName, composeReactions, EMPTY_REACTIONS, MAX_ADDS, type MockupBlock, type MockupView, type Reactions } from "@/lib/cards";

/**
 * A mock-up of the result the person wants, with made-up values, built from
 * blocks the API has already cut to fit. Every column, field, recipient and
 * figure can be tapped: keep, drop, or say what is wrong; every block can
 * take an addition. The reactions go back as one message, which the AI files
 * into what they want. Nothing here builds anything or writes to the brief.
 */

type Editing = { kind: "wrong"; target: string } | { kind: "add"; block: string } | null;

const CHANNEL = { email: Mail, whatsapp: MessageCircle, sms: MessageSquare, screen: Monitor } as const;

export function Mockup({
  view,
  latest,
  canReact,
  onSend,
}: {
  view: MockupView;
  latest: boolean;
  canReact: boolean;
  onSend: (text: string, data: { toolCallId: string } & Reactions) => void;
}) {
  const [r, setR] = useState<Reactions>(EMPTY_REACTIONS);
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [draft, setDraft] = useState("");

  const without = (t: string, x: Reactions): Reactions => ({
    ...x,
    keep: x.keep.filter((k) => k !== t),
    drop: x.drop.filter((k) => k !== t),
    wrong: x.wrong.filter((w) => w.target !== t),
  });
  const mark = (t: string, kind: "keep" | "drop") => {
    setR((x) => ({ ...without(t, x), [kind]: [...without(t, x)[kind], t] }));
    setOpen(null);
  };
  const save = () => {
    const note = draft.trim();
    if (editing && note) {
      setR((x) =>
        editing.kind === "wrong"
          ? { ...without(editing.target, x), wrong: [...without(editing.target, x).wrong, { target: editing.target, note }] }
          : x.add.length >= MAX_ADDS
            ? x
            : { ...x, add: [...x.add, { block: editing.block, note }] },
      );
    }
    setEditing(null);
    setOpen(null);
    setDraft("");
  };
  const stateOf = (t: string) =>
    r.keep.includes(t) ? "keep" : r.drop.includes(t) ? "drop" : r.wrong.some((w) => w.target === t) ? "wrong" : null;

  // A render function, not a component: a component defined inside render is
  // remounted on every state change and loses its popover.
  const target = (label: string, key: string) => {
    if (!canReact) return <span>{label}</span>;
    const s = stateOf(label);
    return (
      <span className="relative inline-block">
        <button
          type="button"
          onClick={() => setOpen(open === key ? null : key)}
          className={cn(
            "hover:bg-accent rounded px-0.5 text-left decoration-dotted underline-offset-2",
            // One text-decoration-line: a dropped item is struck through, the rest underlined.
            s === "drop" ? "text-muted-foreground line-through decoration-solid" : "underline",
            s === "keep" && "text-primary",
            s === "wrong" && "text-destructive",
          )}
        >
          {label}
        </button>
        {open === key && !editing ? (
          <span className="bg-card border-border absolute top-full left-0 z-20 mt-1 flex gap-1 rounded-md border p-1 shadow-lg">
            <Button size="sm" variant="ghost" onClick={() => mark(label, "keep")}>Keep</Button>
            <Button size="sm" variant="ghost" onClick={() => mark(label, "drop")}>Drop</Button>
            <Button size="sm" variant="ghost" onClick={() => { setDraft(""); setEditing({ kind: "wrong", target: label }); }}>Wrong…</Button>
          </span>
        ) : null}
      </span>
    );
  };

  const prompt = editing?.kind === "wrong" ? `What is wrong with ${editing.target}?` : editing ? `What should ${editing.block} also show?` : "";

  const renderBlock = (b: MockupBlock, i: number) => {
    const name = blockName(b);
    const body = (() => {
      switch (b.type) {
        case "figures":
          return (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {b.items.map((it, k) => (
                <div key={k} className="bg-secondary rounded-md p-2">
                  <div className="text-lg font-semibold">{it.value}</div>
                  <div className="text-muted-foreground text-xs">{target(it.label, `${i}:figure:${k}`)}</div>
                </div>
              ))}
            </div>
          );
        case "table":
          return (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr>{b.columns.map((c, k) => <th key={k} className="px-1.5 py-1 text-left font-medium">{target(c, `${i}:column:${k}`)}</th>)}</tr>
                </thead>
                <tbody>
                  {b.rows.map((row, ri) => (
                    <tr key={ri} className="border-border border-t">{row.map((cell, ci) => <td key={ci} className="px-1.5 py-1">{cell}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        case "fields":
          return (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
              {b.fields.map((f, k) => (
                <div key={k} className="contents">
                  <dt className="font-medium">{target(f.label, `${i}:field:${k}`)}</dt>
                  <dd className="border-border text-muted-foreground rounded border px-1.5">{f.example}</dd>
                </div>
              ))}
            </dl>
          );
        case "message": {
          const Icon = CHANNEL[b.channel];
          return (
            <div className="bg-secondary rounded-md p-2 text-xs">
              <div className="text-muted-foreground mb-1 flex flex-wrap items-center gap-1">
                <Icon size={12} /> To {b.to.length ? b.to.map((t, k) => <span key={k}>{k ? ", " : ""}{target(t, `${i}:to:${k}`)}</span>) : "—"}
              </div>
              {b.subject ? <div className="font-medium">{b.subject}</div> : null}
              <p className="whitespace-pre-wrap">{b.text}</p>
            </div>
          );
        }
      }
    })();
    return (
      <section key={i} className="space-y-1">
        {"title" in b && b.title ? <h4 className="text-xs font-medium">{b.title}</h4> : null}
        {body}
        {canReact && r.add.length < MAX_ADDS ? (
          <Button size="sm" variant="ghost" onClick={() => { setDraft(""); setEditing({ kind: "add", block: name }); }}>Add…</Button>
        ) : null}
      </section>
    );
  };

  const text = composeReactions(view.title, r, latest);
  return (
    <div className="bg-card border-border mt-2 space-y-3 rounded-lg border p-3">
      <div className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">Mock-up · {view.title}</div>
      {view.blocks.length ? view.blocks.map(renderBlock) : <Prose text={view.markdown ?? ""} />}
      {editing ? (
        <div className="flex items-center gap-2">
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                e.preventDefault();
                save();
              }
            }}
            aria-label={prompt}
            placeholder={prompt}
            maxLength={300}
            className="border-input bg-background h-8 min-w-0 flex-1 rounded-md border px-2 text-xs"
          />
          <Button size="sm" onClick={save}>Save</Button>
          <Button size="sm" variant="ghost" onClick={() => { setEditing(null); setOpen(null); setDraft(""); }}>Cancel</Button>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-[11px]">{canReact && view.blocks.length ? "Made-up numbers. Tap anything to react." : "Made-up numbers. What's missing?"}</p>
        {canReact && view.blocks.length ? (
          <Button size="sm" disabled={!text} onClick={() => { onSend(text, { toolCallId: view.toolCallId, ...r }); setR(EMPTY_REACTIONS); }}>
            Send reactions
          </Button>
        ) : null}
      </div>
    </div>
  );
}
